import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import 'sure-components/styles.css';
import 'react-select/dist/react-select.css';
import './index.css';

import React, { Fragment } from 'react';
import ReactDOM from 'react-dom';
import {
  Router,
  Route,
  IndexRoute,
  IndexRedirect,
  Redirect,
  browserHistory,
} from 'react-router';
import { createStore, combineReducers, applyMiddleware, compose } from 'redux';
import { Provider } from 'react-redux';
import {
  routerMiddleware,
  syncHistoryWithStore,
  routerReducer,
} from 'react-router-redux';
import createLogger from 'redux-logger';
import thunk from 'redux-thunk';
import get from 'lodash/get';
import camelCase from 'lodash/camelCase';
import isEmpty from 'lodash/isEmpty';
import isPlainObject from 'lodash/isPlainObject';
import isUndefined from 'lodash/isUndefined';
import merge from 'lodash/merge';
import replace from 'lodash/replace';
import some from 'lodash/some';
import values from 'lodash/values';
import axios from 'axios';
import { getStoredState, createPersistor, autoRehydrate } from 'redux-persist';
import sessionPersistor from './session_persistor';
import getStateFromUrl from 'shared/lib/urlParamHydrator';
import {
  loadScript,
  getDeviceInfo,
  logException,
  sanitizeSentryStatePayload,
  setSentryUserContext,
} from 'shared/lib/utils';
import localforage from 'localforage';
import * as analytics from 'shared/lib/analytics';
import * as Sentry from '@sentry/browser';

import reducers from './store/reducers';

import HeadTags from './components/HeadTags';

import AccountContainer from './containers/AccountContainer';
import CollectiblesEndorsementContainer from './containers/CollectiblesEndorsementContainer';
import BaggageContainer from './containers/BaggageContainer';
import RentersContainer from './containers/RentersContainer';
import RentalCarContainer from './containers/RentalCarContainer';
import CollectiblesContainer from './containers/CollectiblesContainer';
import BopContainer from './containers/BopContainer';

import Claim from './containers/Claim';
import ClaimsAuthorizationEmail from './containers/ClaimsAuthorizationEmail';
import ClaimsBanking from './containers/ClaimsBanking';
import ClaimsDamageDescription from './containers/ClaimsDamageDescription';
import ClaimsDamages from './containers/ClaimsDamages';
import ClaimsInStoreRepair from './containers/ClaimsInStoreRepair';
import ClaimsIndex from './containers/ClaimsIndex';
import ClaimsNew from './containers/ClaimsNew';
import ClaimsPolicies from './containers/ClaimsPolicies';
import ClaimsPolicy from './containers/ClaimsPolicy';

import UpdatePaymentMethod from './containers/UpdatePaymentMethod';
import UpdatePaymentMethodV2 from './containers/UpdatePaymentMethodV2';

import PoliciesAllVerticalsView from './components/PoliciesAllVerticalsView';
import PoliciesForVerticalView from './components/PoliciesForVerticalView';
import PolicyDetailsView from './components/PolicyDetailsView';
import Settings from './components/Settings';

import Licenses from './components/Licenses';
import ProductsPage from './components/ProductsPage';

import BaggageAddressView from './components/BaggageAddressView';
import BaggageFlightDetailsView from './components/BaggageFlightDetailsView';
import BaggagePassengerDetailsView from './components/BaggagePassengerDetailsView';
import BaggagePlansView from './components/BaggagePlansView';
import BaggagePaymentView from './components/BaggagePaymentView';
import BaggageConfirmationView from './components/BaggageConfirmationView';

import SurveyManager from 'shared/lib/components/SurveyManager';

import {
  collectiblesConfig,
  baggageConfig,
  bopConfig,
  fineArtConfig,
  flightConfig,
  jewelryConfig,
  rentalCarConfig,
  rentersConfig,
} from './constants/landingConfigs';

import LayoutWithNavNew from './containers/LayoutWithNav';
import Landing from './components/Landing';
import LandingConnect from './components/LandingConnect';
import LandingPlatform from './components/LandingPlatform';
import LandingClaims from './components/LandingClaims';
import LandingLeadGen from './components/LandingLeadGen';
import LandingTemplate from './components/LandingTemplate';
import FAQ from './components/FAQ';
import LandingCarrier from './components/LandingCarrier';

import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfUse from './components/TermsOfUse';

import Cancellations from './components/Cancellations/Cancellations';

import CollectiblesEndorsementPropertyInfo from './components/CollectiblesEndorsementPropertyInfo';
import CollectiblesEndorsementPaymentView from './components/CollectiblesEndorsementPaymentView';
import CollectiblesEndorsementConfirmationView from './components/CollectiblesEndorsementConfirmationView';

import RentalCarExtension from './components/RentalCarExtension';
import RentalCarExtensionStart from './components/RentalCarExtensionStart';
import RentalCarExtensionConfirmationNumber from './components/RentalCarExtensionConfirmationNumber';
import RentalCarExtensionPayment from './components/RentalCarExtensionPayment';
import RentalCarExtensionConfirmation from './components/RentalCarExtensionConfirmation';

import LandingUSBG from './components/LandingUSGB';
import LandingIframeComics from './components/LandingIframeComics';
import LandingIframeJewelry from './components/LandingIframeJewelry';
import LandingIframeBOP from './components/LandingIframeBOP';

import Login from './components/Login';

/**
 * Subapp routes
 */

import { rentersRoutesCreator } from 'shared/lib/apps/RentersApp/routes';
import { bopRoutesCreator } from 'shared/lib/apps/BopApp/routes';
import { collectiblesRoutesCreator } from 'shared/lib/apps/CollectiblesApp/routes';
import { rentalCarRoutesCreator } from 'shared/lib/apps/RentalCarApp/routes';

/**
 * Polyfills
 */
import 'react-app-polyfill/ie11';

const ENV = process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development';

/**
 * Build landing pages
 */

const LandingCollectibles = props => {
  const categorySlug = get(props, 'params.categorySlug');

  if (categorySlug) {
    const categoryCode = camelCase(categorySlug.replace('-insurance', ''));
    collectiblesConfig.hero.img = [
      process.env.PUBLIC_URL,
      collectiblesConfig.heroImages[categoryCode],
    ].join('');
  } else {
    collectiblesConfig.hero.img = `${
      process.env.PUBLIC_URL
    }/images/collectibles/hero-other.png`;
  }

  switch (categorySlug) {
    case 'jewelry-insurance':
      return <LandingTemplate {...props} config={jewelryConfig} />;
    case 'fine-art-insurance':
      return <LandingTemplate {...props} config={fineArtConfig} />;
    default:
      return <LandingTemplate {...props} config={collectiblesConfig} />;
  }
};
const LandingBaggage = props => (
  <LandingTemplate {...props} config={baggageConfig} />
);
const LandingBop = props => <LandingTemplate {...props} config={bopConfig} />;
const LandingFlight = props => (
  <LandingTemplate {...props} config={flightConfig} />
);

const LandingRentalCar = props => (
  <LandingTemplate {...props} config={rentalCarConfig} />
);
const LandingRenters = props => (
  <LandingTemplate {...props} config={rentersConfig} />
);

/*
 * Initialize third party
 */

if (!window.Cypress) {
  const apiRegExp = new RegExp(process.env.REACT_APP_SURE_API_BASE_URL);
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: ENV,
    maxBreadcrumbs: 15,
    beforeBreadcrumb: breadcrumb => {
      if (breadcrumb.category === 'xhr') {
        return apiRegExp.test(get(breadcrumb, 'data.url')) ? breadcrumb : null;
      } else {
        return breadcrumb;
      }
    },
    integrations: [
      new Sentry.Integrations.Breadcrumbs({ console: ENV === 'production' }),
    ],
  });
}

window.fbAsyncInit = () => {
  window.FB.init({
    appId: process.env.REACT_APP_FACEBOOK_APP_ID,
    cookie: true,
    xfbml: true,
    version: 'v2.12',
  });
};
loadScript('//connect.facebook.net/en_US/sdk.js');

if (process.env.REACT_APP_SEGMENT_WRITE_KEY && !window.Cypress) {
  analytics.load(process.env.REACT_APP_SEGMENT_WRITE_KEY);
}

window.stripe = window.Stripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

/*
 * Axios config
 */

axios.defaults.baseURL = process.env.REACT_APP_SURE_API_BASE_URL;
axios.defaults.headers.common['X-Environment'] = JSON.stringify(
  getDeviceInfo()
);
axios.interceptors.response.use(
  response => response,
  error => {
    logException(error);
    return Promise.reject(error);
  }
);

/*
 * Initialize store
 */

// XXX: Adds a getState() function to every
// action passed into the reducer
const actionGetStateMiddleware = store => next => action => {
  next({ ...action, getState: store.getState });
};

const storeMiddleware = [
  thunk,
  actionGetStateMiddleware,
  routerMiddleware(browserHistory),
];

if (process.env.REACT_APP_ENV !== 'production') {
  storeMiddleware.push(createLogger());
}

if (process.env.REACT_APP_SENTRY_DSN) {
  storeMiddleware.push(store => next => action => {
    Sentry.addBreadcrumb({
      message: action.type,
      category: 'redux_action',
      level: 'info',
    });
    Sentry.configureScope(scope => {
      scope.setExtra('state', sanitizeSentryStatePayload(store.getState()));
    });
    next(action);
  });
}

const persistConfig = {
  storage: localforage,
  whitelist: ['authToken'],
};

getStoredState(persistConfig, (err, restoredState) => {
  const initialState = merge(
    restoredState,
    sessionPersistor.getSessionState(),
    getStateFromUrl(),
    {
      settings: {
        apiBaseUrl: process.env.REACT_APP_SURE_API_BASE_URL,
      },
    }
  );

  analytics.identify(initialState.account);
  setSentryUserContext(initialState.account);

  if (initialState.authToken) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${
      initialState.authToken
    }`;
  }

  const composeEnhancers =
    process.env.REACT_APP_ENV !== 'production' &&
    window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
      ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
      : compose;

  const store = createStore(
    combineReducers({
      ...reducers,
      routing: routerReducer,
    }),
    initialState,
    composeEnhancers(autoRehydrate(), applyMiddleware(...storeMiddleware))
  );

  createPersistor(store, persistConfig);
  syncHistoryWithStore(browserHistory, store);
  sessionPersistor.createPersistor(store);

  /*
   * Configure routing and mount app
   */

  const ensureLoggedIn = (nextState, stateReplace) => {
    const { authToken } = store.getState();

    if (!authToken) {
      stateReplace({
        pathname: '/login',
      });
    }
  };

  const redirectIfLoggedIn = async (nextState, stateReplace) => {
    const { authToken } = store.getState();
    if (authToken) {
      stateReplace({
        pathname: '/account',
      });
    }
  };

  const redirectIfMissing = opts => {
    return (nextState, stateReplace) => {
      const value = get(store.getState(), opts.key);
      if (
        isEmpty(value) ||
        (isPlainObject(value) && some(values(value), isUndefined))
      ) {
        stateReplace({
          pathname: replace(opts.pathname, /:[a-zA-Z-_]+/, match => {
            return nextState.params[match.slice(1)];
          }),
        });
      }
    };
  };

  ReactDOM.render(
    <Provider store={store}>
      <Fragment>
        <HeadTags />
        <Router onUpdate={() => window.scrollTo(0, 0)} history={browserHistory}>
          {/*XXX: Children of LayoutBare route render TopNavBar themselves bc they have to pass it some non-default props*/}
          <Route path="/" component={LayoutWithNavNew}>
            <IndexRoute component={Landing} onEnter={redirectIfLoggedIn} />
            <Route path="connect" component={LandingConnect} />
            <Route path="cancellations/:policy" component={Cancellations} />
            <Route path="platform" component={LandingPlatform} />
            <Route path="claims" component={LandingClaims} />
            <Route path="contact" component={LandingLeadGen} />
            <Route
              path="about"
              component={() => {
                window.location.href = 'https://sureapp.com/company/about';
                return null;
              }}
            />

            <Route path="privacy" component={PrivacyPolicy} />
            <Route path="insurance-products" component={ProductsPage} />
            <Route path="terms" component={TermsOfUse} />
            <Route path="licenses" component={Licenses} />
            <Route path="faq" component={FAQ} />

            <Route path="carrier" component={LandingCarrier} />
            <Redirect path="partners" to="/platform" />
            <Route
              path="careers"
              exact
              component={() => {
                window.location = 'https://jobs.lever.co/sure';
                return null;
              }}
            />
          </Route>

          <Route
            path="/sign-in"
            component={Login}
            onEnter={redirectIfLoggedIn}
          />

          <Route path="/login" component={Login} onEnter={redirectIfLoggedIn} />

          <Route
            path="/sign-up"
            component={Login}
            onEnter={redirectIfLoggedIn}
          />
          <Route path="/account/reset-password/:token" component={Login} />
          {rentersRoutesCreator({
            container: RentersContainer,
            landingComponent: LandingRenters,
          })}
          {bopRoutesCreator({
            container: BopContainer,
            landingComponent: LandingBop,
          })}
          {collectiblesRoutesCreator({
            container: CollectiblesContainer,
            landingComponent: LandingCollectibles,
            redirectIfMissing,
          })}
          {rentalCarRoutesCreator({
            container: RentalCarContainer,
            landingComponent: LandingRentalCar,
            redirectIfMissing,
          })}
          <Route
            path="/home-warranty"
            component={() => {
              window.location.href = `https://sure.${
                process.env.REACT_APP_DOMAIN
              }.com/product/home-warranty?type=homeowners`;
              return null;
            }}
          />
          <Route path="/flight-insurance" component={LandingFlight} />
          <Route path="/rental-car-insurance" component={RentalCarContainer}>
            <Route path="extension/:policyId" component={RentalCarExtension}>
              <IndexRoute component={RentalCarExtensionStart} />
              <Route
                path="confirmation-number"
                component={RentalCarExtensionConfirmationNumber}
              />
              <Route path="payment" component={RentalCarExtensionPayment} />
              <Route
                path="confirmation"
                component={RentalCarExtensionConfirmation}
              />
            </Route>
          </Route>

          <Route path="/baggage-protection" component={BaggageContainer}>
            <IndexRoute component={LandingBaggage} />
            <Route path="address" component={BaggageAddressView} />
            <Route
              path="flight"
              component={BaggageFlightDetailsView}
              onEnter={redirectIfMissing({
                key: 'baggageLocation',
                pathname: '/baggage-protection/address',
              })}
            />
            <Route
              path="passenger"
              component={BaggagePassengerDetailsView}
              onEnter={redirectIfMissing({
                key: 'baggageAirline',
                pathname: '/baggage-protection/address',
              })}
            />
            <Route
              path="plans"
              component={BaggagePlansView}
              onEnter={redirectIfMissing({
                key: 'baggagePassengerInfo',
                pathname: '/baggage-protection/address',
              })}
            />
            <Route
              path="payment"
              component={BaggagePaymentView}
              onEnter={redirectIfMissing({
                key: 'baggageSelectedPlanId',
                pathname: '/baggage-protection/address',
              })}
            />
            <Route
              path="confirmation"
              component={BaggageConfirmationView}
              onEnter={redirectIfMissing({
                key: 'baggagePurchase',
                pathname: '/baggage-protection/address',
              })}
            />
          </Route>
          <Route
            path="/account"
            component={AccountContainer}
            onEnter={ensureLoggedIn}
          >
            <IndexRedirect to="policies" />
            <Route path="policies" component={PoliciesAllVerticalsView} />
            <Route
              path="policies/:verticalCode"
              component={PoliciesForVerticalView}
            />
            <Route
              path="policies/:verticalCode/:policyId"
              component={PolicyDetailsView}
            />
            <Route
              path="settings"
              component={Settings}
              onEnter={ensureLoggedIn}
            />
            <Route path="claims">
              <IndexRoute component={ClaimsIndex} />
              <Route path="new">
                <IndexRoute component={ClaimsNew} />
                <Route path=":verticalCode">
                  <IndexRoute component={ClaimsPolicies} />
                  <Route path="policies">
                    <IndexRoute component={ClaimsPolicies} />
                    <Route path=":policyNumber">
                      <IndexRoute component={ClaimsPolicy} />
                      <Route
                        path="options"
                        component={ClaimsInStoreRepair}
                        onEnter={redirectIfMissing({
                          key: 'claimsProcessId',
                          pathname: '/account/claims/new',
                        })}
                      />
                      <Route
                        path="damages"
                        component={ClaimsDamages}
                        onEnter={redirectIfMissing({
                          key: 'claimsProcessId',
                          pathname: '/account/claims/new',
                        })}
                      />
                      <Route
                        path="damage-description"
                        component={ClaimsDamageDescription}
                        onEnter={redirectIfMissing({
                          key: 'claimsProcessId',
                          pathname: '/account/claims/new',
                        })}
                      />
                      <Route
                        path="authorization-email"
                        component={ClaimsAuthorizationEmail}
                        onEnter={redirectIfMissing({
                          key: 'claimsProcessId',
                          pathname: '/account/claims/new',
                        })}
                      />
                      <Route
                        path="banking"
                        component={ClaimsBanking}
                        onEnter={redirectIfMissing({
                          key: 'claimsProcessId',
                          pathname: '/account/claims/new',
                        })}
                      />
                    </Route>
                  </Route>
                </Route>
              </Route>
              <Route path=":claimId" component={Claim} />
            </Route>
          </Route>
          <Route
            path="/account/policies/:verticalCode/:policyId/:endorsementAction/:categoryCode"
            component={CollectiblesEndorsementContainer}
            onEnter={ensureLoggedIn}
          >
            <IndexRoute component={CollectiblesEndorsementPropertyInfo} />
            <Route path="survey" component={SurveyManager} />
            <Route
              path="quote"
              component={CollectiblesEndorsementPaymentView}
            />
            <Route
              path="confirmation"
              component={CollectiblesEndorsementConfirmationView}
            />
          </Route>
          <Route path="/verify-email/:token" component={Login} />
          <Route
            path="/update-payment-method"
            component={UpdatePaymentMethod}
          />
          <Route
            path="/update-payment-method-v2"
            component={UpdatePaymentMethodV2}
          />
          <Route
            path="/_/usgb-precious-metals-insurance"
            component={LandingUSBG}
          />
          <Route
            path="/_/comic-book-insurance"
            component={LandingIframeComics}
          />
          <Route path="/_/jewelry-insurance" component={LandingIframeJewelry} />
          <Route
            path="/_/small-business-owners-insurance"
            component={LandingIframeBOP}
          />

          <Redirect path="*" to="/" />
        </Router>
      </Fragment>
    </Provider>,
    document.getElementById('root')
  );
});
